import React, { ReactElement, useContext } from 'react'

import Heading from '../../Common/Components/Heading'
import P from '../../Common/Components/P'
import Fieldset from '../../Common/Components/Fieldset'
import TextInput from '../../Common/Components/TextInput'
import FormRow from '../../Common/Components/FormRow'
import Button from '../../Common/Components/Button'

import { LanguageContext } from '../../Common/Contexts'
import TabActionPromptContext from '../../Common/Components/Tabs/TabActionPromptContext'

import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { AnswerPerformance as AnswerPerformanceLanguage } from '../../../../i18n'
import { HeadingLevel } from '../../Common/Components/Heading/Heading'

/**
 * Component properties type.
 */
type Props = {
    callsInAnsThres1: number | undefined
    setCallsInAnsThres1: (newValue: number | undefined) => void
    callsInAnsThres2: number | undefined
    setCallsInAnsThres2: (newValue: number | undefined) => void
    callsInAnsThres3: number | undefined
    setCallsInAnsThres3: (newValue: number | undefined) => void
    callsInAnsThres4: number | undefined
    setCallsInAnsThres4: (newValue: number | undefined) => void
    callsInAnsThres5: number | undefined
    setCallsInAnsThres5: (newValue: number | undefined) => void
    callsInAnsThres6: number | undefined
    setCallsInAnsThres6: (newValue: number | undefined) => void
    resetAnswerTargets: () => void
}

/**
 * Answer performance section of SLA/KPI tab in report configuration modal window.
 *
 * @param callsInAnsThres1 Report property.
 * @param setCallsInAnsThres1 Setter for corresponding report property.
 * @param callsInAnsThres2 Report property.
 * @param setCallsInAnsThres2 Setter for corresponding report property.
 * @param callsInAnsThres3 Report property.
 * @param setCallsInAnsThres3 Setter for corresponding report property.
 * @param callsInAnsThres4 Report property.
 * @param setCallsInAnsThres4 Setter for corresponding report property.
 * @param callsInAnsThres5 Report property.
 * @param setCallsInAnsThres5 Setter for corresponding report property.
 * @param callsInAnsThres6 Report property.
 * @param setCallsInAnsThres6 Setter for corresponding report property.
 * @param resetAnswerTargets Resets answer targets.
 */
const AnswerPerformance: React.FC<Props> = ({
    callsInAnsThres1,
    setCallsInAnsThres1,
    callsInAnsThres2,
    setCallsInAnsThres2,
    callsInAnsThres3,
    setCallsInAnsThres3,
    callsInAnsThres4,
    setCallsInAnsThres4,
    callsInAnsThres5,
    setCallsInAnsThres5,
    callsInAnsThres6,
    setCallsInAnsThres6,
    resetAnswerTargets
}: Props): ReactElement => {
    const language = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle('AnswerPerformance')
    const { displayPrompt } = useContext(TabActionPromptContext)

    const handleAnswerThresholdsReset = (): void => {
        displayPrompt(
            language.get(AnswerPerformanceLanguage.ResetanswertargetsPrompt),
            language.get(AnswerPerformanceLanguage.ResetanswertargetsPromptButtonConfirmLabel),
            language.get(AnswerPerformanceLanguage.ResetanswertargetsPromptButtonCancelLabel),
            resetAnswerTargets,
            undefined
        )
    }

    return (
        <Fieldset className='answer-performance-section'>
            <Heading level={HeadingLevel.TWO}>
                {language.get(AnswerPerformanceLanguage.AnswerPerformance_SectionHeader)}
            </Heading>
            <P>{language.get(AnswerPerformanceLanguage.AnswerPerformance_SectionPara)}</P>
            <FormRow isInline className={'target'}>
                <TextInput
                    value={callsInAnsThres1}
                    onChange={(value: string): void => setCallsInAnsThres1(parseInt(value, 10) || undefined)}
                    labelLeft={language.get(AnswerPerformanceLanguage.Target1Label)}
                    placeholder={language.get(AnswerPerformanceLanguage.Target1Placeholder)}
                    adornmentEnd={language.get(AnswerPerformanceLanguage.TargetUnit)}
                />
                <TextInput
                    value={callsInAnsThres2}
                    onChange={(value: string): void => setCallsInAnsThres2(parseInt(value, 10) || undefined)}
                    labelLeft={language.get(AnswerPerformanceLanguage.Target2Label)}
                    placeholder={language.get(AnswerPerformanceLanguage.Target2Placeholder)}
                    adornmentEnd={language.get(AnswerPerformanceLanguage.TargetUnit)}
                />
                <TextInput
                    value={callsInAnsThres3}
                    onChange={(value: string): void => setCallsInAnsThres3(parseInt(value, 10) || undefined)}
                    labelLeft={language.get(AnswerPerformanceLanguage.Target3Label)}
                    placeholder={language.get(AnswerPerformanceLanguage.Target3Placeholder)}
                    adornmentEnd={language.get(AnswerPerformanceLanguage.TargetUnit)}
                />
            </FormRow>
            <FormRow isInline className={'target'}>
                <TextInput
                    value={callsInAnsThres4}
                    onChange={(value: string): void => setCallsInAnsThres4(parseInt(value, 10) || undefined)}
                    labelLeft={language.get(AnswerPerformanceLanguage.Target4Label)}
                    placeholder={language.get(AnswerPerformanceLanguage.Target4Placeholder)}
                    adornmentEnd={language.get(AnswerPerformanceLanguage.TargetUnit)}
                />
                <TextInput
                    value={callsInAnsThres5}
                    onChange={(value: string): void => setCallsInAnsThres5(parseInt(value, 10) || undefined)}
                    labelLeft={language.get(AnswerPerformanceLanguage.Target5Label)}
                    placeholder={language.get(AnswerPerformanceLanguage.Target5Placeholder)}
                    adornmentEnd={language.get(AnswerPerformanceLanguage.TargetUnit)}
                />
                <TextInput
                    value={callsInAnsThres6}
                    onChange={(value: string): void => setCallsInAnsThres6(parseInt(value, 10) || undefined)}
                    labelLeft={language.get(AnswerPerformanceLanguage.Target6Label)}
                    placeholder={language.get(AnswerPerformanceLanguage.Target6Placeholder)}
                    adornmentEnd={language.get(AnswerPerformanceLanguage.TargetUnit)}
                />
            </FormRow>
            <Button
                label={language.get(AnswerPerformanceLanguage.ResetanswertargetsButtonLabel)}
                onClick={handleAnswerThresholdsReset}
                isOutlined={false}
            />
        </Fieldset>
    )
}

export default React.memo(AnswerPerformance)
